import React, { useCallback, useMemo } from 'react';

import { WatchQueryFetchPolicy } from '@apollo/client';

import {
  IGetUserMinimalOrdersQuery,
  IGetUserMinimalOrdersQueryVariables,
  useGetUserMinimalOrdersQuery,
} from 'generated/graphql-gateway';
import { UserOrdersDefaultVariables } from 'hooks/use-user-orders';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import logger from 'utils/logger';

import { MinimalRecentOrderData } from './types';
import { mapMinimalOrderToCardData } from './utils';

type MinimalOrderQueryItem = NonNullable<
  NonNullable<IGetUserMinimalOrdersQuery['userMinimalOrders']['orders']>[number]
>;

export const useMinimalRecentItems = (
  enabled: boolean,
  variables: IGetUserMinimalOrdersQueryVariables,
  fetchPolicy: WatchQueryFetchPolicy = 'cache-and-network'
) => {
  const { isAuthenticated } = useAuthContext();
  const {
    indexedMenu,
    indexedOffers,
    indexedRewards,
    isLoading: isUnifiedMenuLoading,
  } = useUnifiedMenuContext();
  const { posServiceMode } = useMenuQueryOptions();
  const [ordersData, setOrdersData] = React.useState<IGetUserMinimalOrdersQuery>();
  const [fetchingMore, setFetchingMore] = React.useState(false);

  const { error, loading, refetch, fetchMore } = useGetUserMinimalOrdersQuery({
    variables: {
      ...UserOrdersDefaultVariables,
      ...variables,
    },
    onCompleted: data => {
      setOrdersData(data);
    },
    context: { shouldNotBatch: true },
    skip: !enabled || !isAuthenticated,
    fetchPolicy,
  });

  const orders = useMemo<MinimalRecentOrderData[]>(() => {
    if (!enabled || isUnifiedMenuLoading) {
      return [];
    }

    return (
      ordersData?.userMinimalOrders.orders?.reduce<MinimalRecentOrderData[]>((acc, order) => {
        if (!order) {
          return acc;
        }

        try {
          acc.push(
            mapMinimalOrderToCardData({
              order: order as MinimalOrderQueryItem,
              menu: indexedMenu,
              indexedOffers,
              indexedRewards,
              posServiceMode,
            })
          );
        } catch (error) {
          logger.warn({
            message: '[useMinimalRecentItems] Failed to map minimal recent order',
            context: {
              rbiOrderId: order.rbiOrderId,
              orderNumberFormatted: order.orderNumberFormatted,
              menuSelectionCount: order.cart.menuSelections?.length ?? 0,
            },
            error,
          });
        }

        return acc;
      }, []) ?? []
    );
  }, [
    enabled,
    indexedMenu,
    indexedOffers,
    indexedRewards,
    isUnifiedMenuLoading,
    ordersData?.userMinimalOrders.orders,
    posServiceMode,
  ]);

  const resumeAfter = ordersData?.userMinimalOrders.resumeAfter;
  const count = ordersData?.userMinimalOrders.count || 0;

  const handleLoadMore = useCallback(async () => {
    if (!resumeAfter) {
      return;
    }

    logEvent(CustomEventNames.LOAD_MORE, EventTypes.Other, {
      'Source Page': '/recentitems',
    });

    setFetchingMore(true);

    try {
      const { data: fetchMoreResult } = await fetchMore({
        variables: { resumeAfter },
      });

      const prevOrders = ordersData?.userMinimalOrders.orders || [];
      const currentOrders = fetchMoreResult.userMinimalOrders.orders || [];

      const nextOrdersData = {
        ...fetchMoreResult,
        userMinimalOrders: {
          ...fetchMoreResult.userMinimalOrders,
          orders: [...prevOrders, ...currentOrders],
        },
      };

      setOrdersData(nextOrdersData);

      return nextOrdersData;
    } catch (error) {
      logger.warn({
        message: '[useMinimalRecentItems] Failed to fetch more minimal recent orders',
        error,
      });
    } finally {
      setFetchingMore(false);
    }
  }, [fetchMore, ordersData?.userMinimalOrders.orders, resumeAfter]);

  return {
    orders,
    error,
    isLoading: enabled ? loading || isUnifiedMenuLoading : false,
    count,
    resumeAfter,
    refetch,
    fetchMore,
    handleLoadMore,
    fetchingMore,
  };
};
